import { useEffect, useState } from "react";

/**
 * Brand colors for non-Tailwind contexts (recharts color arrays, imperative
 * DOM styling) where a `brand-*` utility class can't be used and a literal
 * color string is required.
 *
 * These resolve from the brand CSS custom properties in `client/src/index.css`
 * so they track the token system and adapt to dark mode. We use
 * `--brand-muted-foreground` for the brand blue (it lightens in dark mode for
 * readable lines/text, unlike the constant solid-fill `--brand`) and
 * `--brand-accent` for the brand orange.
 */

function readCssVar(name: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value ? `hsl(${value})` : fallback;
}

// Fallbacks mirror the light-mode token values (`client/src/index.css`) for the
// rare case `getComputedStyle` returns nothing; expressed as HSL (not hex) so
// they stay consistent with the token definitions.
const BRAND_FALLBACK = "hsl(216 100% 40%)";
const BRAND_ACCENT_FALLBACK = "hsl(39 100% 50%)";

/**
 * Default host-configurable accent color (brand blue) used as the fallback when
 * a host hasn't picked their own accent for public-facing branding (booking
 * forms, rooms-for-rent landing, direct-booking settings) and for the Stripe
 * Connect embedded appearance. This is a *data default* for a value the host can
 * override — not a theme-styling token — so it must stay a literal hex (`<input
 * type="color">` and the Stripe appearance API only accept hex, not hsl) and
 * intentionally does not adapt to dark mode. Centralized here as the single
 * source of truth instead of being hardcoded across pages.
 */
// eslint-disable-next-line brand/no-hardcoded-brand-hex -- data default for host-customizable accent; color inputs / Stripe appearance require a literal hex
export const DEFAULT_ACCENT_COLOR = "#0052CC";

/** Imperative resolver for event handlers / non-React code (e.g. map callbacks). */
export function resolveBrandColor(): string {
  return readCssVar("--brand-muted-foreground", BRAND_FALLBACK);
}

export interface BrandColors {
  /** Brand blue — readable line/series color that lightens in dark mode. */
  brand: string;
  /** Brand accent (orange) — constant across themes. */
  brandAccent: string;
}

function snapshot(): BrandColors {
  return {
    brand: readCssVar("--brand-muted-foreground", BRAND_FALLBACK),
    brandAccent: readCssVar("--brand-accent", BRAND_ACCENT_FALLBACK),
  };
}

/**
 * React hook returning brand colors resolved from CSS variables, recomputed
 * whenever the theme (the `dark` class on `<html>`) changes. A MutationObserver
 * is used instead of keying off the theme context so the read happens after the
 * class is actually applied, regardless of effect ordering.
 */
export function useBrandColors(): BrandColors {
  const [colors, setColors] = useState<BrandColors>(snapshot);
  useEffect(() => {
    setColors(snapshot());
    const observer = new MutationObserver(() => setColors(snapshot()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return colors;
}
